import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, MapPin, Clock, AlertTriangle, CheckCircle2, XCircle, TreePalm } from 'lucide-react';

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse-soft text-primary text-lg font-medium">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-800 mb-1">
          Welcome back, {user?.name || 'User'}
        </h1>
        <p className="text-slate-500">
          Here's what's happening with your attendance today
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-[2px] transition-all duration-200 cursor-pointer" data-testid="stat-employees" onClick={() => navigate('/employees')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Total Employees</p>
                <p className="text-3xl font-bold text-slate-800">{stats?.total_employees || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-[2px] transition-all duration-200 cursor-pointer" data-testid="stat-attendance" onClick={() => navigate('/attendance')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Today's Attendance</p>
                <p className="text-3xl font-bold text-slate-800">{stats?.today_attendance || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[#138808]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-[2px] transition-all duration-200 cursor-pointer" data-testid="stat-checked-in" onClick={() => navigate('/attendance')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Currently Checked In</p>
                <p className="text-3xl font-bold text-slate-800">{stats?.checked_in || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-[#FF9933]/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#FF9933]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-[2px] transition-all duration-200 cursor-pointer" data-testid="stat-late" onClick={() => navigate('/attendance')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Late Arrivals</p>
                <p className="text-3xl font-bold text-slate-800">{stats?.late_arrivals || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <Card className="bg-white border border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-all" data-testid="stat-locations" onClick={() => navigate('/locations')}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 mb-1">Active Locations</p>
                <p className="text-2xl font-bold text-slate-800">{stats?.active_locations || 0}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="bg-white border border-slate-200 shadow-sm cursor-pointer hover:shadow-md transition-all" data-testid="stat-pending-leaves" onClick={() => navigate('/leaves')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 mb-1">Pending Leaves</p>
                  <p className="text-2xl font-bold text-slate-800">{stats?.pending_leaves || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center">
                  <TreePalm className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="lg:col-span-2 bg-white border border-slate-200 shadow-sm">
          <CardHeader className="pb-3 cursor-pointer hover:bg-slate-50 transition-colors rounded-t-lg" onClick={() => navigate('/attendance')}>
            <CardTitle className="text-lg font-semibold text-slate-800">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_activity && stats.recent_activity.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_activity.slice(0, 5).map((activity, index) => (
                  <div
                    key={activity.id || index}
                    className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0"
                    data-testid={`activity-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        activity.check_out ? 'bg-red-50' : 'bg-green-50'
                      }`}>
                        {activity.check_out ? (
                          <XCircle className="w-4 h-4 text-red-500" />
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-[#138808]" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">{activity.employee_name}</p>
                        <p className="text-xs text-slate-500">
                          {activity.check_out ? 'Checked out' : 'Checked in'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-700">
                        {formatTime(activity.check_out || activity.check_in)}
                      </p>
                      {activity.is_late && !activity.check_out && (
                        <span className="text-xs text-red-500">Late</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-sm">No activity recorded today</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
